import { useState } from "react";
import { KeyRound, LogIn, CheckCircle2, LogOut, Loader2, HelpCircle, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function KiteExpiredBanner({ show }) {
  const [busy, setBusy] = useState(false);
  if (!show) return null;
  const relogin = async () => {
    setBusy(true);
    try {
      const { url } = await api.kiteLogin();
      window.location.href = url;
    } catch (e) {
      toast.error("Cannot start Kite login", { description: e?.response?.data?.detail });
      setBusy(false);
    }
  };
  return (
    <button
      onClick={relogin}
      disabled={busy}
      className="w-full flex items-center justify-center gap-2 bg-neg/15 border border-neg/50 rounded-sm px-3 py-3 active:scale-[0.99] transition-transform duration-150"
      data-testid="kite-expired-banner"
    >
      {busy ? <Loader2 size={15} className="animate-spin text-neg" /> : <AlertTriangle size={15} className="text-neg" />}
      <span className="text-neg font-bold text-xs tracking-wide">
        {busy ? "OPENING KITE…" : "KITE SESSION EXPIRED — TAP TO RE-LOGIN"}
      </span>
    </button>
  );
}

export function KiteConnect({ kite }) {
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const configured = kite?.configured;
  const loggedIn = kite?.logged_in;

  const login = async () => {
    setBusy(true);
    try {
      const { url } = await api.kiteLogin();
      window.location.href = url; // full redirect (mobile-safe)
    } catch (e) {
      toast.error("Cannot start Kite login", { description: e?.response?.data?.detail || "Keys not configured" });
      setBusy(false);
    }
  };

  const openHelp = async () => {
    setShowHelp((s) => !s);
    if (!diag) {
      try { setDiag(await api.kiteDiagnostics()); } catch (_) {}
    }
  };

  const logout = async () => {
    setBusy(true);
    try {
      await api.kiteLogout();
      toast.success("Logged out of Kite");
    } finally {
      setBusy(false);
    }
  };

  // Keys not added yet
  if (!configured) {
    return (
      <div className="mx-4 mt-3 flex items-center gap-2.5 bg-surface border border-edge rounded-sm px-3 py-2.5" data-testid="kite-card">
        <KeyRound size={16} className="text-warn shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-semibold text-white">Zerodha Kite — not linked</div>
          <div className="text-[10px] text-muted2 truncate">Add API key &amp; secret to enable live login</div>
        </div>
      </div>
    );
  }

  // Configured + logged in
  if (loggedIn) {
    return (
      <div className="mx-4 mt-3 flex items-center justify-between bg-pos/[0.06] border border-pos/30 rounded-sm px-3 py-2.5" data-testid="kite-card">
        <div className="flex items-center gap-2.5">
          <CheckCircle2 size={16} className="text-pos shrink-0" />
          <div>
            <div className="text-xs font-semibold text-pos">Kite connected</div>
            <div className="mono text-[10px] text-muted2">{kite?.user_id || "session active"}</div>
          </div>
        </div>
        <button onClick={logout} disabled={busy} className="flex items-center gap-1 text-[11px] font-bold text-muted2 px-2 py-1 rounded-sm border border-edge active:text-white" data-testid="kite-logout-btn">
          <LogOut size={12} /> LOGOUT
        </button>
      </div>
    );
  }

  // Configured but not logged in today
  return (
    <div className="mx-4 mt-3">
      <button
        onClick={login}
        disabled={busy}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-sm bg-brand text-white font-bold tracking-wider active:scale-[0.99] transition-transform duration-150"
        data-testid="kite-login-btn"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} strokeWidth={2.5} />}
        {busy ? "OPENING KITE…" : "LOGIN TO KITE"}
      </button>
      <button onClick={openHelp} className="mt-1.5 w-full flex items-center justify-center gap-1 text-[11px] text-muted2" data-testid="kite-help-btn">
        <HelpCircle size={12} /> Login not working / not turning green?
      </button>
      {showHelp && (
        <div className="mt-2 bg-surface border border-edge rounded-sm p-3 text-[11px] text-muted2 space-y-2" data-testid="kite-help-panel">
          <div>In your <span className="text-white">Kite developer console → your app → Edit</span>, the <span className="text-white">Redirect URL</span> must be EXACTLY:</div>
          <div className="mono text-[10px] text-pos break-all bg-elevated border border-edge rounded-sm p-2" data-testid="redirect-uri">
            {diag?.expected_redirect_uri || "loading…"}
          </div>
          <div>
            {diag && diag.last_callback === null
              ? "⚠️ Zerodha has NOT redirected back yet — this is almost always a Redirect URL mismatch above. Fix it and retry."
              : diag?.last_callback && !diag.last_callback.ok
              ? `Last attempt failed: ${diag.last_callback.error}`
              : "No trailing slash. Save it, then tap LOGIN TO KITE again."}
          </div>
        </div>
      )}
    </div>
  );
}
